import { CommonModule } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { resolveNavIcon } from '../utils/nav-icon.util';
import { LucideDynamicIcon } from '@lucide/angular';
import { provideNavLucideIcons } from '../utils/nav-lucide-icons';
import { UiTooltipDirective } from '../tooltip/ui-tooltip.directive';

export type UiMenuItemVariant = 'default' | 'child' | 'group' | 'danger' | 'muted';

@Component({
  selector: 'ui-menu-item',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, LucideDynamicIcon, UiTooltipDirective],
  providers: [provideNavLucideIcons()],
  template: `
    @if (link()) {
      <a
        [routerLink]="link()!"
        routerLinkActive="ui-menu-item--active"
        [routerLinkActiveOptions]="{ exact: exactActive() }"
        [class]="itemClasses"
        [uiTooltip]="label()"
        [uiTooltipDisabled]="!collapsed()"
        [uiTooltipPosition]="'right'"
        [attr.aria-label]="collapsed() ? label() : null"
        (click)="onActivate($event)"
      >
        <ng-container *ngTemplateOutlet="itemContent" />
      </a>
    } @else {
      <button
        type="button"
        [class]="itemClasses"
        [attr.aria-expanded]="variant() === 'group' ? expanded() : null"
        [uiTooltip]="label()"
        [uiTooltipDisabled]="!collapsed()"
        [uiTooltipPosition]="'right'"
        [attr.aria-label]="collapsed() ? label() : null"
        [disabled]="disabled()"
        (click)="onActivate($event)"
      >
        <ng-container *ngTemplateOutlet="itemContent" />
      </button>
    }

    <ng-template #itemContent>
      @if (variant() === 'child') {
        <span class="ui-menu-item__dot" aria-hidden="true"></span>
      } @else if (icon()) {
        <span class="ui-menu-item__icon" aria-hidden="true">
          <svg [lucideIcon]="resolvedIcon()" [size]="iconSize()" strokeWidth="2"></svg>
        </span>
      }

      <span class="ui-menu-item__label">{{ label() }}</span>

      @if (badge()) {
        <span class="ui-menu-item__badge">{{ badge() }}</span>
      }

      @if (variant() === 'group') {
        @if (!collapsed()) {
        <svg
          [lucideIcon]="expanded() ? 'chevron-down' : 'chevron-right'"
          [size]="16"
          class="ui-menu-item__chevron"
          aria-hidden="true"
        ></svg>
        }
      }
    </ng-template>
  `,
  styles: [`
    :host {
      display: block;
    }

    .ui-menu-item {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      min-height: 2.5rem;
      width: 100%;
      padding: 0.5rem 0.625rem;
      border-radius: 0.625rem;
      border: 1px solid transparent;
      background: none;
      color: var(--text-primary, #1f2937);
      font-size: 0.875rem;
      line-height: 1.25;
      text-decoration: none;
      text-align: left;
      cursor: pointer;
      position: relative;
      transition: background 0.15s, color 0.15s;
      box-sizing: border-box;
    }

    .ui-menu-item:hover:not(:disabled) {
      background: color-mix(in srgb, var(--brand-primary) 6%, var(--surface-bg, #fff));
      color: var(--brand-primary, #1677ff);
    }

    .ui-menu-item:hover:not(:disabled) .ui-menu-item__icon {
      background: var(--brand-primary, #1677ff);
      color: var(--on-brand-primary, #fff);
    }

    .ui-menu-item:hover:not(:disabled) .ui-menu-item__chevron {
      color: var(--brand-primary, #1677ff);
    }

    .ui-menu-item--active {
      background: color-mix(in srgb, var(--brand-primary) 12%, var(--surface-bg, #fff));
      color: var(--brand-primary, #1677ff);
      font-weight: 600;
    }

    .ui-menu-item--active .ui-menu-item__icon {
      background: color-mix(in srgb, var(--brand-primary) 16%, var(--surface-bg, #fff));
      color: var(--brand-primary, #1677ff);
    }

    .ui-menu-item--active:hover:not(:disabled) .ui-menu-item__icon {
      background: var(--brand-primary, #1677ff);
      color: var(--on-brand-primary, #fff);
    }

    .ui-menu-item--child {
      min-height: 2.25rem;
      padding-left: 2.35rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--text-body, #475569);
    }

    .ui-menu-item--child:hover:not(:disabled) {
      color: var(--brand-primary, #1677ff);
    }

    .ui-menu-item--child:hover:not(:disabled) .ui-menu-item__dot {
      background: var(--brand-primary, #1677ff);
    }

    .ui-menu-item--child.ui-menu-item--active {
      color: var(--brand-primary, #1677ff);
    }

    .ui-menu-item--child.ui-menu-item--active .ui-menu-item__dot {
      background: var(--brand-primary, #1677ff);
    }

    .ui-menu-item--group {
      font-weight: 500;
    }

    .ui-menu-item--muted {
      justify-content: center;
      color: var(--text-muted, #8c8c8c);
      font-size: 0.8125rem;
    }

    .ui-menu-item--muted:hover:not(:disabled) {
      color: var(--text-secondary, #595959);
      background: color-mix(in srgb, var(--brand-primary) 4%, var(--surface-bg, #fff));
    }

    .ui-menu-item--danger {
      color: var(--danger, #ff4d4f);
    }

    .ui-menu-item--danger .ui-menu-item__label {
      font-weight: 500;
    }

    .ui-menu-item--danger .ui-menu-item__icon {
      background: color-mix(in srgb, var(--danger, #ff4d4f) 12%, var(--surface-bg, #fff));
      color: var(--danger, #ff4d4f);
    }

    .ui-menu-item--danger:hover:not(:disabled) {
      background: color-mix(in srgb, var(--danger, #ff4d4f) 8%, var(--surface-bg, #fff));
      color: var(--danger, #ff4d4f);
    }

    .ui-menu-item--danger:hover:not(:disabled) .ui-menu-item__icon {
      background: var(--danger, #ff4d4f);
      color: var(--on-danger, #fff);
    }

    .ui-menu-item--danger:hover:not(:disabled) .ui-menu-item__chevron {
      color: var(--danger, #ff4d4f);
    }

    .ui-menu-item:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    .ui-menu-item__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.5rem;
      flex-shrink: 0;
      color: var(--text-muted, #64748b);
      background: var(--surface-muted, #f8fafc);
      transition: background 0.15s, color 0.15s;
    }

    .ui-menu-item__icon svg {
      stroke: currentColor;
    }

    .ui-menu-item__label {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ui-menu-item__chevron {
      flex-shrink: 0;
      color: var(--text-muted, #94a3b8);
      stroke: currentColor;
    }

    .ui-menu-item__dot {
      position: absolute;
      left: 1.15rem;
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 999px;
      background: var(--border-color, #cbd5e1);
      transition: background 0.15s;
    }

    .ui-menu-item__badge {
      margin-left: auto;
      min-width: 1.25rem;
      padding: 0.125rem 0.375rem;
      border-radius: 999px;
      background: color-mix(in srgb, var(--brand-primary, #1677ff) 14%, var(--surface-bg, #fff));
      color: var(--brand-primary, #1677ff);
      font-size: 0.6875rem;
      font-weight: 700;
      text-align: center;
    }

    .ui-menu-item--icon-only {
      justify-content: center;
      padding-inline: 0.5rem;
    }

    .ui-menu-item--icon-only .ui-menu-item__label,
    .ui-menu-item--icon-only .ui-menu-item__chevron,
    .ui-menu-item--icon-only .ui-menu-item__badge,
    .ui-menu-item--icon-only .ui-menu-item__dot {
      display: none;
    }
  `],
})
export class UiMenuItemComponent {
  label = input.required<string>();
  icon = input<string | undefined>();
  link = input<string | undefined>();
  exactActive = input(false);
  variant = input<UiMenuItemVariant>('default');
  badge = input<string | undefined>();
  expanded = input(false);
  disabled = input(false);
  collapsed = input(false);

  activated = output<void>();

  get itemClasses(): string {
    return [
      'ui-menu-item',
      this.variant() !== 'default' ? `ui-menu-item--${this.variant()}` : '',
      this.collapsed() ? 'ui-menu-item--icon-only' : '',
    ].filter(Boolean).join(' ');
  }

  resolvedIcon(): string {
    return resolveNavIcon(this.icon());
  }

  iconSize(): number {
    return this.variant() === 'child' ? 16 : 18;
  }

  onActivate(event: Event): void {
    if (this.disabled()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.activated.emit();
  }
}
